<template>
  <div class="wrapper">
    <div class="main visual-2 abt-hospital hospital-global-settings-main">
      <Header :backRoute="'/appointments'" />

      <div class="settings-container">
        <!-- <h2 class="mb-4 page-title mt-10">
          <i class="fa-solid fa-gear"></i>Settings
        </h2> -->

        <div v-if="isSuperAdmin" class="hospital-switch-card">
          <div class="hospital-switch-icon">
            <i class="fa-solid fa-hospital"></i>
          </div>
          <div class="hospital-switch-content">
            <h3>Hospital View</h3>
            <p>Select a specific hospital to view its dashboard and manage its data.</p>
          </div>
          <div class="hospital-switch-control">
            <label class="form-label small text-muted">Change Hospital</label>
            <select v-model="selectedHospitalId" class="form-select hospital-dropdown" @change="handleHospitalChange"
              :disabled="isLoadingHospitals">
              <option value="" disabled>-- Choose a Hospital --</option>
              <option v-for="h in hospitals" :key="h.id || h._id" :value="h.id || h._id">
                {{ h.name }}
              </option>
            </select>
            <div v-if="isLoadingHospitals" class="text-muted small mt-2">
              Loading hospitals...
            </div>
          </div>
        </div>

        <div class="settings-grid">

          <router-link to="/timeslots" class="settings-card">
            <i class="fa-solid fa-clock"></i>
            <h5>Time Slots Settings</h5>
            <p>Manage doctor availability & schedules</p>
          </router-link>

          <router-link to="/appointment-types" class="settings-card">
            <i class="fa-solid fa-stethoscope"></i>
            <h5>Appointment Types Settings</h5>
            <p>Consultation, Follow-up, Emergency etc</p>
          </router-link>

          <router-link to="/queue-counter" class="settings-card">
            <i class="fa-solid fa-list"></i>
            <h5>Queue System</h5>
            <p>Token & walk-in queue configuration</p>
          </router-link>

          <router-link to="/appointment-approval" class="settings-card">
            <i class="fa-solid fa-check"></i>
            <h5>Appointment Approval</h5>
            <p>Auto or manual appointment approval</p>
          </router-link>

          <router-link to="/appointment-dashboard" class="settings-card">
            <i class="fa-solid fa-chart-line"></i>
            <h5>Appointments Dashboard</h5>
            <p>View appointment analytics, utilization and revenue insights</p>
          </router-link>

          <!-- <router-link v-if="canAccessUserManagement" to="/user-management" class="settings-card">
            <i class="fa-solid fa-users-gear"></i>
            <h5>User Management</h5>
            <p>Manage system users, roles and access</p>
          </router-link> -->

          <router-link to="/hospital-profile" class="settings-card">
            <i class="fa-solid fa-hospital"></i>
            <h5>Hospital Profile</h5>
            <p>Name, address, logo & info</p>
          </router-link>

          <router-link to="/add-doctor" class="settings-card">
            <i class="fa-solid fa-user-doctor"></i>
            <h5>Doctors</h5>
            <p>Add, edit or remove doctors</p>
          </router-link>

        </div>
      </div>

      <FooterView />
    </div>
  </div>
</template>

<script>
import Header from "@/layout/Header.vue";
import FooterView from "@/layout/Footer.vue";
import { AuthService } from "@/services/auth.service";

export default {
  components: {
    Header,
    FooterView
  },
  data() {
    return {
      hospitals: [],
      selectedHospitalId: localStorage.getItem("hospitalId") || "",
      isLoadingHospitals: false
    };
  },
  computed: {
    currentUserRole() {
      return this.$store?.state?.user?.currentUser?.objectType || "";
    },
    isSuperAdmin() {
      return ["superadmin", "super_admin", "super-admin"].includes(
        this.currentUserRole
      );
    },
    canAccessUserManagement() {
      return [
        "admin",
        "superadmin",
        "super_admin",
        "super-admin",
        "hospital"
      ].includes(this.currentUserRole);
    }
  },
  mounted() {
    if (this.isSuperAdmin) {
      this.fetchHospitals();
    }
  },
  methods: {
    async fetchHospitals() {
      this.isLoadingHospitals = true;
      try {
        const response = await AuthService.getSuperAdminHospitals();
        this.hospitals = response.data?.items || response.data || [];
      } catch (error) {
        console.error("Error fetching hospitals:", error);
        this.$store.commit("toast/NEW", {
          type: "error",
          message: "Failed to load hospitals list"
        });
      } finally {
        this.isLoadingHospitals = false;
      }
    },
    async handleHospitalChange() {
      if (!this.selectedHospitalId) return;

      try {
        const response = await AuthService.updateActiveHospital(this.selectedHospitalId);

        if (response.success) {
          const responseData = response.data || {};
          const token = responseData.token || responseData.jwt;
          const user = responseData.user || responseData.payload;
          const hospitalId =
            responseData.activeHospital?.id ||
            user?.hospitalId ||
            this.selectedHospitalId;

          if (token) {
            localStorage.setItem("token", token);
          }

          if (user) {
            this.$store.commit("user/SET_CURRENT_USER", user);
          }

          localStorage.setItem("hospitalId", hospitalId);
          await AuthService.getProfile();

          this.$store.commit("toast/NEW", {
            type: "success",
            message: "Hospital selected successfully!"
          });

          this.$router.push("/appointments");
        } else {
          throw new Error(response.message || "Failed to update active hospital");
        }
      } catch (error) {
        console.error("Error updating active hospital:", error);
        this.$store.commit("toast/NEW", {
          type: "error",
          message: error.message || "Failed to update active hospital"
        });
      }
    }
  }
};
</script>

<style scoped>
.page-title {
  font-size: 28px;
  font-weight: 600;
  color: #0144A6;
}

.hospital-global-settings-main {
  background-attachment: fixed;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  height: auto;
  min-height: 100vh;
  overflow-x: hidden;
}

.settings-container {
  max-width: 1200px;
  margin: auto;
  padding: 30px;
  margin-top: 40px;
  padding-bottom: 100px;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
}

.hospital-switch-card {
  align-items: center;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  display: grid;
  gap: 18px;
  grid-template-columns: auto 1fr minmax(260px, 360px);
  margin-bottom: 24px;
  padding: 22px;
}

.hospital-switch-icon {
  align-items: center;
  background: rgba(40, 167, 69, 0.12);
  border-radius: 14px;
  color: #7890e0;
  display: flex;
  font-size: 28px;
  height: 64px;
  justify-content: center;
  width: 64px;
}

.hospital-switch-content h3 {
  color: #0144A6;
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 6px;
}

.hospital-switch-content p {
  color: #777;
  font-size: 14px;
  margin: 0;
}

.hospital-switch-control label {
  display: block;
  margin-bottom: 6px;
}

.hospital-dropdown {
  border: 1px solid #ddd;
  border-radius: 8px;
  min-height: 42px;
}

.settings-card {
  background: #fff;
  border-radius: 14px;
  padding: 25px;
  text-align: center;
  text-decoration: none;
  color: #2c3e50;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  transition: 0.25s;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.settings-card i {
  font-size: 28px;
  margin-bottom: 12px;
  color: #3498db;
}

.settings-card h5 {
  font-weight: 600;
  margin-bottom: 6px;
}

.settings-card p {
  font-size: 14px;
  color: #777;
}

.settings-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border: 1px solid #0144A6;
}

@media (max-width: 768px) {
  .hospital-switch-card {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .hospital-switch-icon {
    margin: 0 auto;
  }

}
</style>
